import { useEffect, useRef, useState } from 'react';

/**
 * Poll an async fetcher on an interval, returning { data, loading, error }.
 *
 * Fetches immediately on mount, then every `intervalMs`. Errors are swallowed
 * into `error` state (never thrown) so a widget can't crash the page that hosts
 * it. Cleans up the interval and ignores in-flight results after unmount.
 *
 * @param {() => Promise<any>} fetchFn  stable fetcher (e.g. () => getSportsTicker())
 * @param {number} intervalMs           poll interval in ms (default 60s)
 */
export default function usePolling(fetchFn, intervalMs = 60000) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const fetchRef = useRef(fetchFn);
  fetchRef.current = fetchFn;

  useEffect(() => {
    let alive = true;

    async function tick() {
      try {
        const result = await fetchRef.current();
        if (!alive) return;
        setData(result);
        setError(null);
      } catch (err) {
        if (!alive) return;
        setError(err);
      } finally {
        if (alive) setLoading(false);
      }
    }

    tick();
    const id = setInterval(tick, intervalMs);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [intervalMs]);

  return { data, loading, error };
}
